import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TableSortLabel,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowBack, Add, EditOutlined, DeleteOutlined } from '@mui/icons-material';
import useSortTable from '../../../hooks/useSortTable';
import retentionService from '../services/retentionService';
import RetentionLineTable from '../components/RetentionLineTable';
import RetentionLineForm from '../components/RetentionLineForm';
import proceedingService from '../../proceedings/services/proceedingService';
import ProceedingModalForm from '../../proceedings/components/ProceedingModalForm';
import companyService from '../../companies/services/companyService';
import PageMasthead from '../../../components/PageMasthead';

const RetentionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [retention, setRetention] = useState(null);
  const [retentionLines, setRetentionLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [proceedings, setProceedings] = useState([]);
  const [openProceedingModal, setOpenProceedingModal] = useState(false);
  const [selectedProceeding, setSelectedProceeding] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [retentions, setRetentions] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  const { sorted: sortedProceedings, sortBy, sortDir, handleSort } = useSortTable(proceedings);

  const SortCell = ({ label, field }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  useEffect(() => {
    loadRetention();
    loadRetentionLines();
    loadProceedings();
    loadCompanies();
  }, [id]);

  const loadProceedings = async () => {
    try {
      const result = await proceedingService.getAll({ retentionId: id, limit: 50 });
      setProceedings(result.data || []);
    } catch (error) {
      console.error('Error loading proceedings:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const result = await companyService.getAll({ limit: 100 });
      setCompanies(result.data || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadRetentionsForCompany = async (companyId) => {
    try {
      const result = await retentionService.getAll({ companyId, limit: 100 });
      setRetentions(result.data || []);
    } catch (error) {
      console.error('Error loading retentions:', error);
      setRetentions([]);
    }
  };

  const handleCreateProceeding = () => {
    setSelectedProceeding(null);
    if (retention?.companyId) {
      loadRetentionsForCompany(retention.companyId);
    }
    setOpenProceedingModal(true);
  };

  const handleEditProceeding = (proceeding) => {
    setSelectedProceeding(proceeding);
    setOpenProceedingModal(true);
  };

  const handleDeleteProceeding = async (proceedingId) => {
    if (!window.confirm('¿Eliminar este expediente?')) return;
    try {
      await proceedingService.delete(proceedingId);
      showSnackbar('Expediente eliminado');
      loadProceedings();
    } catch (error) {
      console.error('Error deleting proceeding:', error);
      showSnackbar('Error al eliminar el expediente', 'error');
    }
  };

  const handleSaveProceeding = async (data) => {
    try {
      if (selectedProceeding) {
        await proceedingService.update(selectedProceeding.id, data);
        showSnackbar('Expediente actualizado exitosamente');
      } else {
        await proceedingService.create(data);
        showSnackbar('Expediente creado exitosamente');
      }
      setOpenProceedingModal(false);
      setSelectedProceeding(null);
      loadProceedings();
    } catch (error) {
      console.error('Error saving proceeding:', error);
      throw error;
    }
  };

  const loadRetention = async () => {
    try {
      setLoading(true);
      const data = await retentionService.getById(id);
      setRetention(data.data);
    } catch (error) {
      console.error('Error loading retention:', error);
      showSnackbar('Error al cargar la tabla de retencion', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadRetentionLines = async () => {
    try {
      const lines = await retentionService.getLines(id);
      setRetentionLines(lines);
    } catch (error) {
      console.error('Error loading retention lines:', error);
      showSnackbar('Error al cargar las lineas de retencion', 'error');
    }
  };

  const handleCreateLine = () => {
    setSelectedLine(null);
    setOpenModal(true);
  };

  const handleEditLine = (line) => {
    setSelectedLine(line);
    setOpenModal(true);
  };

  const handleSaveLine = async (data) => {
    try {
      if (selectedLine) {
        await retentionService.updateLine(selectedLine.id, data);
        showSnackbar('Linea actualizada exitosamente');
      } else {
        await retentionService.createLine(id, data);
        showSnackbar('Linea creada exitosamente');
      }
      setOpenModal(false);
      loadRetentionLines();
    } catch (error) {
      console.error('Error saving line:', error);
      showSnackbar(error.response?.data?.error || 'Error al guardar la linea', 'error');
      throw error;
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('¿Esta seguro de eliminar esta linea de retencion?')) return;
    try {
      await retentionService.deleteLine(lineId);
      showSnackbar('Linea eliminada exitosamente');
      loadRetentionLines();
    } catch (error) {
      console.error('Error deleting line:', error);
      showSnackbar(error.response?.data?.error || 'Error al eliminar la linea', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const InfoField = ({ label, children }) => (
    <Box>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(29,31,32,0.55)', mb: 0.5 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!retention) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Tabla de retencion no encontrada</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Masthead */}
      <PageMasthead
        title={retention.name}
        subtitle={`Tabla de retencion documental · ${retention.code}`}
      />

      {/* Back row */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/retentions')}
          sx={{ color: '#5980a6', fontWeight: 700, fontSize: '0.9375rem' }}
        >
          Retencion
        </Button>
      </Box>

      {/* Info card */}
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: '20px', mb: 3 }}>
          <InfoField label="Empresa">
            <Typography variant="body1">{retention.company?.name || '\u2014'}</Typography>
          </InfoField>
          <InfoField label="Codigo">
            <Typography variant="body1">{retention.code}</Typography>
          </InfoField>
          <InfoField label="Area">
            <Typography variant="body1">{retention.area?.name || '\u2014'}</Typography>
          </InfoField>
          <InfoField label="Fecha">
            <Typography variant="body1">{new Date(retention.date).toLocaleDateString('es-ES', { timeZone: 'UTC' })}</Typography>
          </InfoField>
        </Box>

        {retention.comments && (
          <Box sx={{ borderTop: '1px solid rgba(29,31,32,0.16)', pt: 2.5 }}>
            <InfoField label="Comentarios">
              <Typography variant="body1">{retention.comments}</Typography>
            </InfoField>
          </Box>
        )}
      </Paper>

      {/* Tabs */}
      <Box sx={{ mb: 3, display: 'inline-flex' }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Lineas de retencion" />
          <Tab label="Expedientes" />
        </Tabs>
      </Box>

      {/* Tab: Lineas */}
      {tabIndex === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Lineas de Retencion
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateLine}>
              Nueva Linea
            </Button>
          </Box>
          <RetentionLineTable
            lines={retentionLines}
            onEdit={handleEditLine}
            onDelete={handleDeleteLine}
          />
        </>
      )}

      {/* Tab: Expedientes */}
      {tabIndex === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Expedientes ({proceedings.length})
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateProceeding}>
              Nuevo Expediente
            </Button>
          </Box>
          {proceedings.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay expedientes asociados a esta tabla de retencion.
            </Typography>
          ) : (
            <Paper sx={{ overflow: 'hidden' }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortCell label="Nombre" field="name" />
                      <SortCell label="Codigo" field="code" />
                      <SortCell label="Fecha Inicio" field="startDate" />
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedProceedings.map((p) => (
                      <TableRow
                        key={p.id}
                        hover
                        onClick={() => navigate(`/proceedings/${p.id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600 }}>
                            {p.name}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2">{p.code}</Typography></TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{p.startDate ? new Date(p.startDate).toLocaleDateString('es-ES', { timeZone: 'UTC' }) : '\u2014'}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => handleEditProceeding(p)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                <EditOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" onClick={() => handleDeleteProceeding(p.id)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                <DeleteOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      <RetentionLineForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSaveLine}
        line={selectedLine}
      />

      <ProceedingModalForm
        open={openProceedingModal}
        onClose={() => { setOpenProceedingModal(false); setSelectedProceeding(null); }}
        onSave={handleSaveProceeding}
        proceeding={selectedProceeding || {
          companyId: retention?.companyId || '',
          retentionLine: { retentionId: retention?.id },
        }}
        companies={companies}
        retentions={retentions}
        onCompanyChange={loadRetentionsForCompany}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RetentionDetail;
